import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

// All supported file types
const SUPPORTED_FILE_TYPES = [
  '.txt', '.md', '.markdown', '.pdf', '.doc', '.docx',
  '.xls', '.xlsx', '.ppt', '.pptx', '.png', '.jpg',
  '.jpeg', '.tiff', '.bmp'
];

const SUPPORTED_FILE_TYPES_DISPLAY = 'TXT, MD, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG, JPEG, TIFF, BMP';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export default function AddContentModal({ open, onClose, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      file: null,
      documentUrl: '',
      title: '',
    },
    mode: 'onChange',
  });

  const file = watch('file');
  const title = watch('title');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file size
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error('File size exceeds 50MB limit');
        event.target.value = null;
        return;
      }

      // Validate file type
      const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();

      if (!SUPPORTED_FILE_TYPES.includes(fileExt)) {
        toast.error(`Invalid file type. Supported types: ${SUPPORTED_FILE_TYPES_DISPLAY}`);
        event.target.value = null;
        return;
      }

      setValue('file', selectedFile);

      // Auto-populate title from filename if title is empty
      if (!title) {
        const fileName = selectedFile.name.replace(/\.[^/.]+$/, '');
        setValue('title', fileName);
      }
    }
  };

  const handleClose = () => {
    if (!uploading) {
      reset();
      setUploadProgress(0);
      onClose();
    }
  };

  const onSubmit = async (data) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      // Ingest document
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('title', data.title);
      if (data.documentUrl) {
        formData.append('url', data.documentUrl);
      }

      // Simulate progress (since we don't have real progress tracking)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      await api.admin.ingestDocument(formData);
      clearInterval(progressInterval);
      setUploadProgress(100);

      toast.success('Document added successfully');

      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Failed to add document:', error);
      toast.error(error.message || 'Failed to add document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const isSubmitDisabled = () => {
    if (!title || !file) return true;
    return false;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Add Document</span>
          <IconButton onClick={handleClose} disabled={uploading} size="small">
            <X className="w-5 h-5" />
          </IconButton>
        </div>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <div className="space-y-4">
            {/* Title */}
            <Controller
              name="title"
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title *"
                  placeholder="Enter document title"
                  fullWidth
                  disabled={uploading}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />

            {/* Document URL (optional) */}
            <Controller
              name="documentUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Document URL (optional)"
                  placeholder="Enter document URL"
                  fullWidth
                  disabled={uploading}
                />
              )}
            />

            {/* File Input */}
            <div>
              <input
                type="file"
                onChange={handleFileChange}
                disabled={uploading}
                accept={SUPPORTED_FILE_TYPES.join(',')}
                style={{ display: 'block', marginBottom: '8px' }}
              />
              <FormHelperText>
                <strong>Supported file types:</strong> {SUPPORTED_FILE_TYPES_DISPLAY}
              </FormHelperText>
              <FormHelperText>
                Max file size: 50MB
              </FormHelperText>
              {!file && <FormHelperText error>File is required</FormHelperText>}
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <FormHelperText>Uploading... {uploadProgress}%</FormHelperText>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitDisabled() || uploading}
          >
            {uploading ? 'Uploading...' : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

